
import { InitProductSlickCarousel } from "@aspen/Aspen/InitSlickCarousel";
import AspenProductCarouselProduct from "@aspen/Store/Aspen/Components/AspenProductCarouselProduct";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, useShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendImpressionTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendImpressionTracking";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import {
    ProductCarouselProductStyles,
} from "@insite/content-library/Components/ProductCarouselProduct";
import { productPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { createRef, useContext, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    carouselType = "carouselType",
    displayProductsFrom = "displayProductsFrom",
    selectedCategoryIds = "selectedCategoryIds",
    relatedProductType = "relatedProductType",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    seedWithManuallyAssigned = "seedWithManuallyAssigned",
    maxNumberOfColumns = "maxNumberOfColumns",
    widgetPosition = "widgetPosition",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showPrice = "showPrice",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
    titleTextColor = "titleTextColor",
    desktopBackgroundColor = "desktopBackgroundColor",
    mobileBackgroundColor = "mobileBackgroundColor",
    arrowColor = "arrowColor",
    productDetailsTextColor = "productDetailsTextColor",
    productDetailsTextHoverColor = "productDetailsTextHoverColor",
    viewAllLink = "viewAllLink",
    viewAllLinkColor = "viewAllLinkColor",
    viewAllLinkHoverColor = "viewAllLinkHoverColor",
    viewAllLinkText = "viewAllLinkText"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.carouselType]: string;
        [fields.displayProductsFrom]: string;
        [fields.selectedCategoryIds]: string[];
        [fields.relatedProductType]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.seedWithManuallyAssigned]: string;
        [fields.maxNumberOfColumns]: number;
        [fields.widgetPosition]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
        [fields.titleTextColor]: string;
        [fields.desktopBackgroundColor]: string;
        [fields.mobileBackgroundColor]: string;
        [fields.arrowColor]: string;
        [fields.productDetailsTextColor]: string;
        [fields.productDetailsTextHoverColor]: string;
        [fields.viewAllLink]: LinkFieldValue;
        [fields.viewAllLinkColor]: string;
        [fields.viewAllLinkHoverColor]: string;
        [fields.viewAllLinkText]: string;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    productRecommendationWidgets: state.components.productRecommendations.widgets,
    session: state.context.session,
});

const mapDispatchToProps = {
    loadCarouselProducts,
    sendImpressionTracking,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductCarouselStyles {
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    carouselStyles?: InjectableCss;
    topSellerStyles?: InjectableCss;
}

const ProductCarousel = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    productRecommendationWidgets,
    sendImpressionTracking,
    session,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = useContext(BrandStateContext).value;
    const category = useContext(CategoryContext);
    const productContext = useContext(ProductContext);
    const { url } = useGetLink(fields.viewAllLink);
    const history = useHistory();
    const { isInShell } = useShellContext();

    const onClick = () => {
        if (!url) {
            return;
        }

        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };


    const productCarouselStyles: ProductCarouselStyles = {
        carouselStyles: {
            css: css`
                a {
                    background-color: transparent;
                    color: ${fields.productDetailsTextColor};
                    transition: color .35s cubic-bezier(.3,.86,.36,.95);
                    -webkit-text-decoration: none;
                    text-decoration: none;
                    &:hover {
                        -webkit-text-decoration: none;
                        text-decoration: none;
                        color: ${fields.productDetailsTextHoverColor};
                    }
                    span{
                        color:unset!important;
                    }
                }
                strong {
                    font-weight: bolder;
                }
                img {
                    border-style: none;
                    display: inline-block;
                    height: auto;
                    max-width: 100%;
                }
                button {
                    font-family: inherit;
                    font-size: 100%;
                    line-height: 1.15;
                    margin: 0;
                    overflow: visible;
                    text-transform: none;
                    -webkit-appearance: button;
                }
                .slick-slider {
                    -webkit-touch-callout: none;
                    -webkit-tap-highlight-color: rgba(0,0,0,0);
                    box-sizing: border-box;
                    touch-action: pan-y;
                    -webkit-user-select: none;
                    -moz-user-select: none;
                    user-select: none;
                    display: block;
                    position: relative;
                    .slick-list {
                        transform: translateZ(0);
                    }
                    .slick-track {
                        transform: translateZ(0);
                    }
                }
                .slick-list {
                    display: block;
                    position: relative;
                    margin: 0;
                    overflow: hidden;
                    padding: 0;
                    &:focus {
                        outline: none;
                    }
                }
                .slick-track {
                    display: block;
                    left: 0;
                    position: relative;
                    top: 0;
                    &:after {
                        content: "";
                        display: table;
                        clear: both;
                    }
                    &:before {
                        content: "";
                        display: table;
                    }
                }
                .slick-slide {
                    display: none;
                    float: left;
                    height: 100%;
                    min-height: 1px;
                    img {
                        display: block;
                    }
                }
                .slick-initialized {
                    .slick-slide {
                        display: block;
                    }
                }
                .slick-arrow {
                    background-color: transparent;
                    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='15.672' height='29.384'%3E%3Cpath fill='none' stroke='${fields.arrowColor?.replace('#', '%23')}' stroke-linecap='round' stroke-width='1.8' d='m1.272 1.273 13.14 13.7-13.14 13.14'/%3E%3C/svg%3E");
                    background-repeat: no-repeat;
                    background-size: 100%;
                    border: 0;
                    cursor: pointer;
                    font-size: 0;
                    height: 30px;
                    padding: 0;
                    position: absolute;
                    right: 0;
                    top: 50%;
                    transform: translateY(-50%);
                    transition: opacity .3s ease;
                    width: 15px;
                    z-index: 10;
                    &:hover {
                        opacity: .8;
                    }
                }
                .slick-arrow.slick-prev {
                    left: 0;
                    transform: rotateY(180deg) translateY(-50%);
                }
                .slick-arrow.slick-next {
                    right: 0;
                }
                * {
                    box-sizing: inherit;
                }
                &:after {
                    box-sizing: inherit;
                }
                &:before {
                    box-sizing: inherit;
                }
                h2 {
                    color: ${fields.titleTextColor};
                    font-weight: inherit;
                    margin: 0 0 10px;
                    font-weight: 700;
                    line-height: 1.26;
                }
                .as-container {
                    &:after {
                        clear: both;
                        content: "";
                        display: block;
                    }
                    margin: 0 auto;
                    max-width: 1180px;
                    padding-left: 20px;
                    padding-right: 20px;
                    width: 100%;
                }
                &.as-bg-grey-alt {
                    background: ${fields.mobileBackgroundColor};
                }
                &.as-history-section {
                    padding: 38px 0 19px;
                    h2 {
                        font-size: 32px;
                        font-weight: 500;
                        line-height: 1.24;
                        margin: 0 0 16px;
                        text-align: center;
                    }
                }
                .as-history-carousel {
                    margin: 0 -20px;
                    padding: 0 0 0 67px;
                    position: relative;
                    .as-slide {
                        padding: 0 5px;
                        text-align: center;
                        width: 207px;
                    }
                    .as-image {
                        background: #fff;
                        border-radius: 50%;
                        box-shadow: 0 6px 6px rgba(0,42,83,.16);
                        display: block;
                        height: 175px;
                        margin: 0 auto 13px;
                        width: 175px;
                    }
                    .as-image-wrap {
                        align-items: center;
                        border-radius: 50%;
                        display: flex;
                        height: 100%;
                        justify-content: center;
                        overflow: hidden;
                        width: 100%;
                        object-fit: contain;
                    }
                    img {
                        max-height: 100%;
                        max-width: 100%;
                        vertical-align: top;
                    }
                    .as-ttl {
                        display: block;
                        font-size: 14px;
                        font-weight: 500;
                        letter-spacing: .18px;
                        margin: 0;
                        a {
                            color: ${fields.productDetailsTextColor};
                            transition: color .3s ease;
                            &:hover {
                            color: ${fields.productDetailsTextHoverColor};
                            }
                        }
                    }
                    .as-text {
                        display: block;
                        font-family: "Inter Tight", Arial;
                        font-size: 14px;
                        font-weight: 400;
                        color: ${fields.productDetailsTextColor};
                        letter-spacing: .16px;
                        line-height: 1.2;
                        margin: 0;
                    }
                }
                @media (min-width:768px) {
                    &.as-history-section {
                        padding: 38px 0 82px;
                        h2 {
                            font-size: 42px;
                            margin: 0 0 46px;
                        }
                    }
                    .as-history-carousel {
                        margin: 0 -10px;
                        padding: 0 10px;
                        .as-slide {
                            padding: 2px 15px 0;
                            width: auto;
                        }
                        .as-image {
                            height: 234px;
                            margin: 0 auto 31px;
                            width: 234px;
                        }
                        .as-ttl {
                            font-size: 18px;
                            margin: 0 0 5px;
                        }
                        .as-text {
                            font-size: 16px;
                            line-height: 1.4;
                            margin: 0 0 4px;
                            color: ${fields.productDetailsTextColor};
                        }
                        .slick-arrow {
                            top: 117px;
                        }
                    }
                }
                @media (min-width:1024px) {
                    .slick-arrow {
                        background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='21.473' height='37.781'%3E%3Cpath fill='${fields.arrowColor?.replace('#', '%23')}' d='M21.473 18.897a2.925 2.925 0 0 1-.959 2.005l-1.989 1.989Q11.56 29.865 4.581 36.829a4.669 4.669 0 0 1-.631.511c-.056.04-.108.076-.16.116a1.6 1.6 0 0 1-.967.328h-.475a1.6 1.6 0 0 1-.639-.136 2.538 2.538 0 0 1-1.7-2.516 3.032 3.032 0 0 1 .387-1.262 2.34 2.34 0 0 1 .555-.7l.036-.032 14.252-14.252Q8.1 11.753.966 4.609l-.048-.044a3.976 3.976 0 0 1-.4-.443A2.565 2.565 0 0 1 4.082.503a3.983 3.983 0 0 1 .478.426l15.966 15.955a2.88 2.88 0 0 1 .947 2.013'/%3E%3C/svg%3E");
                        height: 38px;
                        width: 21px;
                    }
                    h2 {
                        font-size: 23px;
                    }
                    &.as-bg-grey-alt {
                        background: ${fields.desktopBackgroundColor};
                    }
                }
                @media (min-width:1280px) {
                    .as-history-carousel {
                        margin: 0 -65px;
                        padding: 0 35px;
                    }
                }
                @media (max-width:767px) {
                    .as-history-carousel {
                        .slick-list {
                            overflow: visible;
                        }
                    }
                }
            `
        },
        topSellerStyles: {
            css: css`
                a {
                    background-color: transparent;
                    color: ${fields.productDetailsTextColor};
                    transition: color .35s cubic-bezier(.3,.86,.36,.95);
                    -webkit-text-decoration: none;
                    text-decoration: none;
                    &:hover {
                        -webkit-text-decoration: none;
                        text-decoration: none;
                        color: ${fields.productDetailsTextHoverColor};
                    }
                    span{
                        color:unset!important;
                    }
                }
                .as-container:after {
                    clear: both;
                    content: "";
                    display: block;
                }
     
                .as-container {
                    margin: 0 auto;
                    max-width: 1180px;
                    padding-left: 20px;
                    padding-right: 20px;
                    width: 100%;
                }
     
                &.as-product-section {
                    padding: 36px 0 13px;
                }
     
                .as-heading {
                    display: flex;
                    justify-content: space-between;
                    margin: 0 0 15px;
                }
     
                .as-heading .as-btn-view {
                    margin-top: 4px;
                }
     
                .as-heading h2 {
                    color: ${fields.titleTextColor};
                    margin-bottom: 0;
                    max-width: 80%;
                }
     
                .as-btn-view {
                    color: ${fields.viewAllLinkColor};
                    font-size: 23px;
                    font-weight: 700;
                    transition: color 0.3s ease;
                }
     
                .as-btn-view:hover {
                    color: ${fields.viewAllLinkHoverColor};
                }
     
                .as-product-list {
                    display: flex;
                    flex-wrap: wrap;
                    margin: 0 -6px;
                }
     
                .as-product-item {
                    padding: 0 6px 20px;
                    width: 50%;
                }
     
                .as-product-item .as-product-image {
                    border: 1px solid #e5e6e5;
                    display: block;
                    margin-bottom: 9px;
                }
     
                .as-product-item img {
                    height: 100%;
                    -o-object-fit: cover;
                    object-fit: cover;
                    vertical-align: top;
                    width: 100%;
                }
     
                .as-product-item .as-product-ttl {
                    display: block;
                    font-size: 15px;
                    font-weight: 500;
                    letter-spacing: 0.18px;
                    line-height: 1.44;
                    margin-bottom: 4px;
                }
     
                .as-product-item .as-product-txt {
                    display: block;
                    font-size: 15px;
                    line-height: 1.2;
                    margin-bottom: 7px;
                    color: ${fields.productDetailsTextColor};
                }
     
                @media (min-width: 768px) {
                    &.as-product-section {
                        padding: 67px 0 61px;
                    }
     
                    .as-heading {
                        margin: 0 0 79px;
                    }
     
                    .as-heading .as-btn-view {
                        margin-top: 10px;
                    }
     
                    .as-product-list {
                        margin: 0 -10px;
                    }
     
                    .as-product-item {
                        padding: 0 10px 20px;
                        width: 33.33%;
                    }
     
                    .as-product-item .as-product-image {
                        margin-bottom: 14px;
                    }
     
                    .as-product-item .as-product-ttl {
                        font-size: 16px;
                    }
                }
     
                @media (min-width: 1024px) {
                    .as-h1 {
                        font-size: 32px;
                        letter-spacing: 0;
                        line-height: 1.22;
                    }
     
                    h2 {
                        font-size: 23px;
                    }
     
                    .as-product-item .as-product-ttl {
                        font-size: 18px;
                    }
     
                    .as-product-item .as-product-txt {
                        font-size: 16px;
                        color: ${fields.productDetailsTextColor};
                    }
                }
            `,
        }
    };

    const styles = productCarouselStyles;

    useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType: fields.carouselType,
            relatedProductType: fields.relatedProductType,
            seedWithManuallyAssigned: fields.seedWithManuallyAssigned,
            displayProductsFrom: fields.displayProductsFrom,
            selectedCategoryIds: fields.selectedCategoryIds,
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            widgetPosition: fields.widgetPosition,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [
        fields.carouselType,
        fields.relatedProductType,
        fields.seedWithManuallyAssigned,
        fields.displayProductsFrom,
        fields.selectedCategoryIds,
        fields.numberOfProductsToDisplay,
        fields.widgetPosition,
        productContext?.product?.id,
        category?.id,
        brand?.id,
        productRecommendationWidgets,
        session?.pickUpWarehouse?.id,
    ]);

    useEffect(() => {
        if (
            fields.carouselType !== "recommendedProducts" ||
            !productRecommendationWidgets ||
            fields.widgetPosition > productRecommendationWidgets.length ||
            !products
        ) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        const recommendationIds: number[] = [];
        products?.forEach(o => {
            const id = productRecommendationWidget.recs.find(r => r.refCode === o.detail?.name)?.id;
            if (id) {
                recommendationIds.push(id);
            }
        });

        if (recommendationIds.length) {
            sendImpressionTracking({ recommendationIds });
        }
    }, [fields.carouselType, productRecommendationWidgets, products]);
    useEffect(() => {
        let isSlickCarouselInitiated = false;
        const interval = setInterval(() => {
            if (typeof window === 'object' && typeof document === 'object' && !isSlickCarouselInitiated) {
                const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
                InitProductSlickCarousel(maxNumberOfColumns,id);
                isSlickCarouselInitiated = true;
                clearInterval(interval);
            }
        }, 300);
    }, [fields.maxNumberOfColumns,id]);

    const getProductRecommendationId = (product: ProductModel) => {
        if (!productRecommendationWidgets || fields.widgetPosition > productRecommendationWidgets.length) {
            return;
        }

        const productRecommendationWidget = productRecommendationWidgets[fields.widgetPosition - 1];
        return productRecommendationWidget.recs.find(r => r.refCode === product.detail?.name)?.id;
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const title =
        fields.title ||
        translate(carouselTypeOptions.find(o => o.value === fields.carouselType)?.displayName || "Product Carousel");
    const showBrandBlocks = fields.showBrandName && products.some(o => !!o.brand);
    
    if (fields.carouselType === "topSellers") {
        return (
            <Typography as="section" className="as-product-section" {...styles.topSellerStyles}>
                <div className="as-container">
                    <div className="as-heading">
                        <h2 className="as-h1">{fields.title}</h2>
                        <a href={url} className="as-btn-view">
                            {fields.viewAllLinkText}
                        </a>
                    </div>
                    <div className="as-product-list">
                        {products.slice(0, 3).map(product => (
                            <div className="as-product-item" key={product.id} id={`product_${product.id}`}
                                data-test-key={product.id}
                                data-test-selector="carousel_slide">
                                <AspenProductCarouselProduct
                                    carouselId={id}
                                    product={product}
                                    recommendationId={getProductRecommendationId(product)}
                                    showImage={fields.showImage}
                                    showBrand={showBrandBlocks}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={session.isAuthenticated?fields.showPrice:false}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    extendedStyles={styles.carouselProductStyles}
                                    carouselType={fields.carouselType}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </Typography>
        );
    }
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <Typography as="section" className="as-history-section as-bg-grey-alt" {...styles.carouselStyles}>
                <div className="as-container" data-test-selector={`productCarousel_${fields.carouselType}${fields.relatedProductType}`}>
                    <h2>{title}</h2>
                    <div className="as-history-carousel" data-test-selector="slides" id={id}>
                        {products.map(product => (
                            <div className="as-slide" key={product.id} id={`product_${product.id}`}
                                data-test-key={product.id}
                                data-test-selector="carousel_slide">
                                <AspenProductCarouselProduct
                                    carouselId={id}
                                    product={product}
                                    recommendationId={getProductRecommendationId(product)}
                                    showImage={fields.showImage}
                                    showBrand={showBrandBlocks}
                                    showTitle={fields.showTitle}
                                    showPartNumbers={fields.showPartNumbers}
                                    showPrice={session.isAuthenticated?fields.showPrice:false}
                                    showAddToCart={fields.showAddToCart}
                                    showAddToList={fields.showAddToList}
                                    showImageBadges={fields.showImageBadges}
                                    maximumNumberOfImageBadges={fields.maximumNumberOfImageBadges}
                                    showTextBadges={fields.showTextBadges}
                                    maximumNumberOfTextBadges={fields.maximumNumberOfTextBadges}
                                    extendedStyles={styles.carouselProductStyles}
                                    carouselType={fields.carouselType}
                                />
                            </div>
                        ))}

                    </div>
                </div>
            </Typography>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const carouselTypeOptions = [
    { value: "crossSells", displayName: "Cross Sells" },
    { value: "customersAlsoPurchased", displayName: "Customers Also Purchased" },
    { value: "recentlyViewed", displayName: "Recently Viewed" },
    { value: "relatedProducts", displayName: "Related Products" },
    { value: "topSellers", displayName: "Top Sellers" },
    { value: "recommendedProducts", displayName: "Recommended Products" },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ProductCarousel))),
    definition: {
        group: "Common",
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                sortOrder: 0,
            },
            {
                name: fields.titleTextColor,
                displayName: "Title Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#253746",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.carouselType,
                displayName: "Carousel Type",
                editorTemplate: "DropDownField",
                options: carouselTypeOptions,
                defaultValue: "crossSells",
                fieldType: "Contextual",
                sortOrder: 0,
                customFilter: (item, _, settings) => {
                    const {
                        settingsCollection: {
                            websiteSettings: { enableDynamicRecommendations },
                            productSettings: { enableProductRecommendations },
                        },
                    } = settings;

                    if (!enableDynamicRecommendations && item.value === "topSellers") {
                        return false;
                    }

                    if (!enableProductRecommendations && item.value === "recommendedProducts") {
                        return false;
                    }

                    return true;
                },
            },
            {
                name: fields.productDetailsTextColor,
                displayName: "Product Details Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#253746",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.productDetailsTextHoverColor,
                displayName: "Product Details Text Hover Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "#c8102e",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.desktopBackgroundColor,
                displayName: "Desktop Widget Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "rgba(152,164,174,.5)",
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "General",
                sortOrder: 0,
            }
            ,
            {
                name: fields.mobileBackgroundColor,
                displayName: "Mobile Widget Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "rgba(152,164,174,.25)",
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "General",
                sortOrder: 0,
            }
            ,
            {
                name: fields.arrowColor,
                displayName: "Arrow Color (Desktop)",
                editorTemplate: "ColorPickerField",
                defaultValue: "#c8102e",
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "General",
                sortOrder: 0,
            },
            
            {
                name: fields.displayProductsFrom,
                displayName: "Display Products From",
                editorTemplate: "DropDownField",
                options: [
                    { value: "allCategories", displayName: "All Categories" },
                    { value: "selectedCategories", displayName: "Selected Categories" },
                    { value: "customerSegments", displayName: "Customer Segments" },
                ],
                defaultValue: "allCategories",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 2,
            },
            {
                name: fields.viewAllLink,
                fieldType: "General",
                displayName: "View All Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                isRequired: false,
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                tooltip: "Select the link for view all",
            },
            {
                name: fields.viewAllLinkText,
                displayName: "View All Link Text",
                editorTemplate: "TextField",
                defaultValue: "View All",
                fieldType: "General",
                tooltip: "Enter the Label for top sellers widget",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                isRequired: false,
            },
            {
                name: fields.viewAllLinkColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#253746",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 0,
            },
            {
                name: fields.viewAllLinkHoverColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "#c8102e",
                fieldType: "General",
                isVisible: item => item.fields[fields.carouselType] === "topSellers",
                sortOrder: 0,
            },
            {
                name: fields.selectedCategoryIds,
                displayName: "Selected Categories",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "topSellers" &&
                    item.fields[fields.displayProductsFrom] === "selectedCategories",
                sortOrder: 3,
            },
            {
                name: fields.relatedProductType,
                displayName: "Related Product Type",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "relatedProducts",
                sortOrder: 4,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                defaultValue: 10,
                fieldType: "Contextual",
                isVisible: item =>
                    item.fields[fields.carouselType] === "customersAlsoPurchased" ||
                    item.fields[fields.carouselType] === "topSellers",
                sortOrder: 5,
            },
            {
                name: fields.seedWithManuallyAssigned,
                displayName: "Seed With Manually Assigned",
                editorTemplate: "SystemListDropDownField",
                systemListName: "ProductRelationship",
                defaultValue: "",
                fieldType: "Contextual",
                isVisible: (item, _advancedFeaturesEnabled, _theme, pageType) =>
                    pageType === "ProductDetailsPage" && item.fields[fields.carouselType] === "customersAlsoPurchased" && item.fields[fields.carouselType] !== "topSellers",
                sortOrder: 6,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 6,
                defaultValue: 4,
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "Contextual",
                sortOrder: 7,
            },
            {
                name: fields.widgetPosition,
                displayName: "Widget Position",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 1,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] === "recommendedProducts" && item.fields[fields.carouselType] !== "topSellers",
                sortOrder: 8,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 9,
            },
            {
                name: fields.showTitle,
                displayName: "Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "Contextual",
                sortOrder: 10,
            },
            {
                name: fields.showBrandName,
                displayName: "Brand Name",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 11,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 12,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                sortOrder: 13,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                sortOrder: 14,
            },
            {
                name: fields.showAddToList,
                displayName: "Add to List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "Contextual",
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                sortOrder: 15,
            },
            {
                name: fields.showImageBadges,
                displayName: "Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "Contextual",
                sortOrder: 15,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 16,
            },
            {
                name: fields.showTextBadges,
                displayName: "Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                isVisible: item => item.fields[fields.carouselType] !== "topSellers",
                fieldType: "Contextual",
                sortOrder: 17,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "Contextual",
                sortOrder: 18,
            }
        ],
    },
};

export default widgetModule;
